import { Directive, ElementRef, inject, input, output, signal, DestroyRef } from '@angular/core'
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { filter, map, switchMap, tap, delay } from 'rxjs/operators'

const MIN_HEADER_HEIGHT_DEFAULT = 96

@Directive({
  selector: '[mcmHeaderCollapse]',
  exportAs: 'mcmHeaderCollapse',
  host: {
    '[style.overflow]': '"hidden"',
    '[style.transition]': '"height ease 0.3s"',
    '[class.is-header-collapse]': 'true',
    '[class.is-collapsed]': 'isCollapsed()',
    '[class.has-scrolled]': 'hasScrolled()',
    '[style.height.px]': 'height()',
    '[style.min-height.px]': 'minHeight()'
  }
})
export class HeaderCollapseDirective {
  private readonly _el = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly _destroyRef = inject(DestroyRef)

  readonly minHeight = input(MIN_HEADER_HEIGHT_DEFAULT)
  readonly contentInput = input<any>(undefined, { alias: 'mcmHeaderCollapse' })
  readonly loading = input<boolean>(undefined)

  readonly onScroll = output<number>()

  readonly isCollapsed = signal(false)
  readonly hasScrolled = signal(false)
  readonly height = signal<number | null>(null)

  scrollElem: HTMLElement | undefined
  private _headerOriginalHeight = 0
  private _maxHeight = 0

  constructor() {
    toObservable(this.loading)
      .pipe(
        filter((isLoading) => isLoading === false),
        delay(0),
        tap(() => {
          this._headerOriginalHeight = this._el.nativeElement.getBoundingClientRect().height
          this._maxHeight = Math.max(this._headerOriginalHeight, this._maxHeight)

          this._updatePaddingTop()
          setTimeout(() => this._updatePaddingTop(), 100)
        }),
        takeUntilDestroyed()
      )
      .subscribe()

    let isFirstScroll = false

    toObservable(this.contentInput)
      .pipe(
        filter((content) => !!content),
        tap((content) => {
          content.getScrollElement().then((elem: HTMLElement) => (this.scrollElem = elem))
          content.scrollEvents = true
        }),
        switchMap((content) =>
          content.ionScroll.pipe(
            map((event: any) => event.detail.scrollTop as number),
            tap((scrollTop: number) => {
              this.onScroll.emit(scrollTop)

              const maxScroll = this.maxScrollHeight
              const isCol = !!(maxScroll && scrollTop >= maxScroll - this.minHeight())

              this.isCollapsed.set(isCol)
              this.hasScrolled.set(scrollTop > 0 && !isCol)

              if (scrollTop && maxScroll && this.scrollElem) {
                this.height.set(Math.max(0, maxScroll - scrollTop))
              } else {
                this.height.set(null)
              }

              if (!isFirstScroll) {
                isFirstScroll = true
                this._updatePaddingTop()
              }
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe()
  }

  get maxScrollHeight() {
    return this._el.nativeElement.scrollHeight
  }

  get offsetHeight() {
    return this._el.nativeElement.offsetHeight
  }

  get content() {
    return this.contentInput()
  }

  expand() {
    this.isCollapsed.set(false)
    this.hasScrolled.set(false)
    const targetHeight = Math.max(this._headerOriginalHeight, this._maxHeight)
    this.height.set(targetHeight)
    this._updatePaddingTop(targetHeight)
  }

  collapse() {
    this.isCollapsed.set(true)
    this.hasScrolled.set(false)
    const targetHeight = this.minHeight()
    this.height.set(targetHeight)
    this._updatePaddingTop(targetHeight)
  }

  private _updatePaddingTop(exactHeight?: number) {
    const content = this.contentInput()
    if (content?.el) {
      const paddingTop = exactHeight ?? Math.max(this.offsetHeight, this._maxHeight)
      content.el.style.cssText = `--padding-top: ${paddingTop}px`
    }
  }
}
